import _ from 'lodash'
import { position } from 'polished'
import { theme, numericBreakpoints } from '../../gatsby-plugin-theme-ui/theme'
import { useWindowSize } from 'react-use'
import { useMemo } from 'react'
import { OpaqueInterpolation } from 'react-spring'

const positioningMeasurements = {
  offTableUserInfoHeight: [65, 65, 75, 75, 100],
  offTableUserInfoWidth: [55, 55, 85, 85, 110],
  globalHorizontalPadding: [theme.space[1], theme.space[1], theme.space[3], theme.space[4]],
  globalVerticalPadding: [
    theme.space[5],
    theme.space[5],
    theme.space[5],
    theme.space[6],
    theme.space[6],
  ],
  playingCardWidth: [30, 30, 30, 30, 50],
}

interface IBasePositioningMeasurements {
  offTableUserInfoHeight: number
  offTableUserInfoWidth: number
  globalHorizontalPadding: number
  globalVerticalPadding: number
  playingCardWidth: number
}

export type IPositionCoords = { left: number; top: number }
export type IPositionAnimationCoords = {
  left: OpaqueInterpolation<string | number | undefined>
  top: OpaqueInterpolation<string | number | undefined>
}

export interface IPositioningMeasurements extends IBasePositioningMeasurements {
  tableAndPlayersWidth: number
  tableAndPlayersHeight: number
  tableWidth: number
  tableHeight: number
  horizontalTablePadding: number
  verticalTablePadding: number
  centerOfTable: IPositionCoords
  centerOfLeftTableCircle: IPositionCoords
  centerOfRightTableCircle: IPositionCoords
  centerOfTopTableCircle: IPositionCoords
  centerOfBottomTableCircle: IPositionCoords
  windowHeight: number
  windowWidth: number
  isLandscapeMode: boolean
}

export interface IUserPositioning {
  position: {
    top: number
    left: number
  }
  calcPositionFromPixels: (
    pixels: number,
  ) => {
    top: number
    left: number
  }
}

export interface IPositionProps {
  measurements: IPositioningMeasurements
  otherUserPositioning: IUserPositioning[]
  currentUserTablePositioning: IUserPositioning
}

const getPositioningMeasurements = (
  windowSize: {
    width: number
    height: number
  },
  hasGameChatVisible: boolean,
): IPositioningMeasurements => {
  const currentBreakpointIndex = numericBreakpoints.findIndex(
    (breakpoint) => windowSize.width < breakpoint,
  )
  const getStyleFromMaybeArray = <T extends any>(val: T | T[]) => {
    if (_.isArray(val)) {
      // TODO make this dynamic based off screen-size
      return _.isNil(val[currentBreakpointIndex])
        ? val[val.length - 1]
        : val[currentBreakpointIndex]
    }
    return val
  }
  const accum = _.transform(
    positioningMeasurements,
    (accum, value, key) => {
      accum[key] = getStyleFromMaybeArray(value)
    },
    {} as Record<string, number>,
  )
  // @ts-ignore
  const base = accum as IBasePositioningMeasurements
  const hasChatThatOverlays = windowSize.width <= numericBreakpoints[4]
  const usableWindowWidth =
    windowSize.width - (hasGameChatVisible && !hasChatThatOverlays ? 300 : 0)
  let tableAndPlayersWidth = usableWindowWidth
  let tableAndPlayersHeight = 0.65 * windowSize.height
  if (windowSize.height > windowSize.width) {
    tableAndPlayersHeight = 0.65 * windowSize.height
    tableAndPlayersWidth = _.min([
      (windowSize.width < 370 ? 0.8 : 0.9) * tableAndPlayersHeight,
      tableAndPlayersWidth,
    ])!
  } else {
    if (tableAndPlayersWidth * 0.5 < tableAndPlayersHeight) {
      tableAndPlayersHeight = tableAndPlayersWidth * 0.5
    }
    if (tableAndPlayersWidth > tableAndPlayersHeight * 2.5) {
      tableAndPlayersWidth = tableAndPlayersHeight * 2.5
    }
  }
  const horizontalTablePadding = base.offTableUserInfoWidth + base.globalHorizontalPadding
  const verticalTablePadding = base.offTableUserInfoHeight + base.globalVerticalPadding
  const tableWidth = tableAndPlayersWidth - horizontalTablePadding * 2
  const tableHeight = tableAndPlayersHeight - verticalTablePadding
  const centerOfTable = {
    left: (tableWidth + horizontalTablePadding * 2) / 2,
    top: (tableHeight + verticalTablePadding * 2) / 2,
  }
  const lengthOfNonCircularPartOfTable = Math.abs(tableWidth - tableHeight)
  const centerOfLeftTableCircle = {
    top: centerOfTable.top,
    left: centerOfTable.left - lengthOfNonCircularPartOfTable / 2,
  }
  const centerOfRightTableCircle = {
    top: centerOfTable.top,
    left: centerOfTable.left + lengthOfNonCircularPartOfTable / 2,
  }
  const centerOfTopTableCircle = {
    top: centerOfTable.top - lengthOfNonCircularPartOfTable / 2,
    left: centerOfTable.left,
  }
  const centerOfBottomTableCircle = {
    top: centerOfTable.top + lengthOfNonCircularPartOfTable / 2,
    left: centerOfTable.left,
  }
  const results: IPositioningMeasurements = {
    ...base,
    tableAndPlayersWidth,
    tableAndPlayersHeight,
    tableHeight,
    tableWidth,
    verticalTablePadding,
    horizontalTablePadding,
    centerOfTable,
    centerOfRightTableCircle,
    centerOfLeftTableCircle,
    centerOfTopTableCircle,
    centerOfBottomTableCircle,
    windowHeight: windowSize.height,
    windowWidth: windowSize.width,
    isLandscapeMode: tableHeight < tableWidth,
  }
  return results
}

const degreeToRadians = (degree: number) => (Math.PI / 180) * degree
const slope = (positionA: IPositionCoords, positionB: IPositionCoords) => {
  if (positionB.left === positionA.left) {
    return 0
  }
  const slope = (positionB.top - positionA.top) / (positionB.left - positionA.left)
  return slope
}

const distance = (positionA: IPositionCoords, positionB: IPositionCoords) => {
  const a = positionA.left - positionB.left
  const b = positionA.top - positionB.top
  return Math.sqrt(a * a + b * b)
}

const generateCalcPositionFromPixels = (
  positionA: IPositionCoords,
  positionB: IPositionCoords,
  dampen?: (distanceInPixels: number) => number,
) => (distanceInPixels: number) => {
  // https://math.stackexchange.com/a/1630886
  const t =
    ((dampen ? dampen(distanceInPixels) : distanceInPixels) * -1) / distance(positionA, positionB)
  const [left, top] = [
    (1 - t) * positionA.left + t * positionB.left,
    (1 - t) * positionA.top + t * positionB.top,
  ]
  return { left, top }
}

const desktopCalculateOtherUserTablePositioning = (
  m: IPositioningMeasurements,
  usersCount: number,
): IUserPositioning[] => {
  const radius = m.tableHeight / 2
  const leftCirclePosition = (degree: number) => {
    const position = {
      top: m.centerOfLeftTableCircle.top - radius * Math.sin(degreeToRadians(degree + 90)),
      left: m.centerOfLeftTableCircle.left - radius * Math.cos(degreeToRadians(degree + 90)),
    }
    return {
      position,
      calcPositionFromPixels: generateCalcPositionFromPixels(
        position,
        m.centerOfTable,
        (pixels) => pixels * (pixels < 0 ? 0.8 : 1.2),
      ),
    }
  }

  const rightCirclePosition = (degree: number) => {
    const position = {
      top: m.centerOfRightTableCircle.top + radius * Math.sin(degreeToRadians(degree - 90)),
      left: m.centerOfRightTableCircle.left + radius * Math.cos(degreeToRadians(degree - 90)),
    }
    return {
      position,
      calcPositionFromPixels: generateCalcPositionFromPixels(
        position,
        m.centerOfTable,
        (pixels) => pixels * (pixels < 0 ? 0.8 : 1.2),
      ),
    }
  }

  const tableRatio = (m.tableHeight / m.tableWidth) * 0.2
  const percentageAlongSquareMiddle = (percentage: number) => {
    const distanceFromMiddle = 0.5 - percentage
    const paddedPercentage = percentage + tableRatio * distanceFromMiddle
    // const paddedPercentage = percentage
    const position = {
      top: m.verticalTablePadding,
      left:
        m.centerOfLeftTableCircle.left +
        (m.centerOfRightTableCircle.left - m.centerOfLeftTableCircle.left) * paddedPercentage,
    }
    return {
      position,
      calcPositionFromPixels: generateCalcPositionFromPixels(
        position,
        { left: m.centerOfTable.left, top: m.centerOfTable.top + 0.5 * m.tableHeight },
        (pixels) => pixels * (pixels < 0 ? 0.6 : 1) + Math.abs(0.5 - percentage) * pixels * 0.4,
      ),
    }
  }

  switch (usersCount) {
    case 1:
      return [leftCirclePosition(270)]
    case 2:
      return [leftCirclePosition(315), rightCirclePosition(45)]
    case 3:
      return [leftCirclePosition(270), percentageAlongSquareMiddle(0.5), rightCirclePosition(90)]

    case 4:
      return [
        leftCirclePosition(270),
        percentageAlongSquareMiddle(0),
        percentageAlongSquareMiddle(1),
        rightCirclePosition(90),
      ]

    case 5:
      return [
        leftCirclePosition(180 + 60),
        leftCirclePosition(180 + 130),
        percentageAlongSquareMiddle(0.5),
        rightCirclePosition(180 - 130),
        rightCirclePosition(180 - 60),
      ]
    case 6:
      return [
        leftCirclePosition(180 + 60),
        leftCirclePosition(180 + 125),
        percentageAlongSquareMiddle(0.15),
        percentageAlongSquareMiddle(0.85),
        rightCirclePosition(180 - 125),
        rightCirclePosition(180 - 60),
      ]

    case 7:
      return [
        leftCirclePosition(180 + 60),
        leftCirclePosition(180 + 115),
        percentageAlongSquareMiddle(0.05),
        percentageAlongSquareMiddle(0.5),
        percentageAlongSquareMiddle(0.95),
        rightCirclePosition(180 - 115),
        rightCirclePosition(180 - 60),
      ]

    case 8:
      return [
        leftCirclePosition(180 + 40),
        leftCirclePosition(180 + 90),
        leftCirclePosition(180 + 140),
        percentageAlongSquareMiddle(0.2),
        percentageAlongSquareMiddle(0.8),
        rightCirclePosition(180 - 140),
        rightCirclePosition(180 - 90),
        rightCirclePosition(180 - 40),
      ]
    case 9:
      return [
        leftCirclePosition(180 + 40),
        leftCirclePosition(180 + 90),
        leftCirclePosition(180 + 140),
        percentageAlongSquareMiddle(0.1),
        percentageAlongSquareMiddle(0.5),
        percentageAlongSquareMiddle(0.9),
        rightCirclePosition(180 - 140),
        rightCirclePosition(180 - 90),
        rightCirclePosition(180 - 40),
      ]
    default:
      return []
  }
}

const mobileCalculateOtherUserTablePositioning = (
  m: IPositioningMeasurements,
  usersCount: number,
): IUserPositioning[] => {
  const radius = m.tableWidth / 2
  const tableRatio = (m.tableHeight / m.tableWidth) * 0.2
  const percentageDownSquareLeft = (percentage: number) => {
    const distanceFromMiddle = 0.5 - percentage
    const paddedPercentage = percentage + tableRatio * distanceFromMiddle
    // const paddedPercentage = percentage
    const position = {
      top:
        m.centerOfTopTableCircle.top +
        (m.centerOfBottomTableCircle.top - m.centerOfTopTableCircle.top) * paddedPercentage,
      left: m.horizontalTablePadding,
    }
    return {
      position,
      calcPositionFromPixels: generateCalcPositionFromPixels(
        position,
        m.centerOfTable,
        (pixels) =>
          pixels * 0.4 * (pixels < 0 ? 1 : 1.7) + Math.abs(0.5 - percentage) * pixels * 0.2,
      ),
    }
  }

  const percentageDownSquareRight = (percentage: number) => {
    const distanceFromMiddle = 0.5 - percentage
    const paddedPercentage = percentage + tableRatio * distanceFromMiddle
    // const paddedPercentage = percentage
    const position = {
      top:
        m.centerOfTopTableCircle.top +
        (m.centerOfBottomTableCircle.top - m.centerOfTopTableCircle.top) * paddedPercentage,
      left: m.horizontalTablePadding + m.tableWidth,
    }
    return {
      position,
      calcPositionFromPixels: generateCalcPositionFromPixels(
        position,
        m.centerOfTable,
        (pixels) =>
          pixels * 0.4 * (pixels < 0 ? 1 : 1.7) + Math.abs(0.5 - percentage) * pixels * 0.2,
      ),
    }
  }

  const topCirclePosition = (degree: number) => {
    const position = {
      top: m.centerOfTopTableCircle.top - radius * Math.sin(degreeToRadians(degree + 90)),
      left: m.centerOfTopTableCircle.left - radius * Math.cos(degreeToRadians(degree + 90)),
    }
    return {
      position,
      calcPositionFromPixels: generateCalcPositionFromPixels(
        position,
        m.centerOfTable,
        (pixels) => pixels * 0.4 * (pixels < 0 ? 1 : 2.1),
      ),
    }
  }

  const bottomCirclePosition = (degree: number) => {
    const position = {
      top: m.centerOfBottomTableCircle.top + radius * Math.sin(degreeToRadians(degree - 90)),
      left: m.centerOfBottomTableCircle.left + radius * Math.cos(degreeToRadians(degree - 90)),
    }
    return {
      position,
      calcPositionFromPixels: generateCalcPositionFromPixels(
        position,
        m.centerOfTable,
        (pixels) => pixels * 0.4 * (pixels < 0 ? 1 : 2.1),
      ),
    }
  }

  switch (usersCount) {
    case 1:
      return [topCirclePosition(0)]
    case 2:
      return [percentageDownSquareLeft(0.1), percentageDownSquareRight(0.1)]
    case 3:
      return [percentageDownSquareLeft(0.5), topCirclePosition(0), percentageDownSquareRight(0.5)]

    case 4:
      return [
        percentageDownSquareLeft(0.8),
        topCirclePosition(0 - 60),
        topCirclePosition(0 + 60),
        percentageDownSquareRight(0.8),
      ]

    case 5:
      return [
        percentageDownSquareLeft(1),
        topCirclePosition(0 - 85),
        topCirclePosition(0),
        topCirclePosition(0 + 85),
        percentageDownSquareRight(1),
      ]
    case 6:
      return [
        bottomCirclePosition(180 + 70),
        percentageDownSquareLeft(0.3),
        topCirclePosition(0 - 45),
        topCirclePosition(0 + 45),
        percentageDownSquareRight(0.3),
        bottomCirclePosition(180 - 70),
      ]

    case 7:
      return [
        bottomCirclePosition(180 + 60),
        percentageDownSquareLeft(0.5),
        topCirclePosition(0 - 70),
        topCirclePosition(0),
        topCirclePosition(0 + 70),
        percentageDownSquareRight(0.5),
        bottomCirclePosition(180 - 60),
      ]

    case 8:
      return [
        bottomCirclePosition(180 + 80),
        percentageDownSquareLeft(0.5),
        topCirclePosition(0 - 80),
        topCirclePosition(0 - 30),
        topCirclePosition(0 + 30),
        topCirclePosition(0 + 80),
        percentageDownSquareRight(0.5),
        bottomCirclePosition(180 - 80),
      ]
    case 9:
      return [
        bottomCirclePosition(180 + 65),
        percentageDownSquareLeft(1),
        percentageDownSquareLeft(0),
        topCirclePosition(0 - 50),
        topCirclePosition(0),
        topCirclePosition(0 + 50),
        percentageDownSquareRight(0),
        percentageDownSquareRight(1),
        bottomCirclePosition(180 - 65),
      ]
    default:
      return []
  }
}

const mobileCalculateCurrentUserPositioning = (m: IPositioningMeasurements): IUserPositioning => {
  const radius = m.tableWidth / 2
  const degree = 180
  const position = {
    top: m.centerOfBottomTableCircle.top + radius * Math.sin(degreeToRadians(degree - 90)),
    left: m.centerOfBottomTableCircle.left + radius * Math.cos(degreeToRadians(degree - 90)),
  }
  return {
    position,
    calcPositionFromPixels: generateCalcPositionFromPixels(
      position,
      m.centerOfTable,
      (pixels) => pixels * 0.4 * (pixels < 0 ? 1 : 2.1),
    ),
  }
}

const desktopCalculateCurrentUserPositioning = (m: IPositioningMeasurements): IUserPositioning => {
  const percentageAlongSquareMiddle = (percentage: number) => {
    const position = {
      top: m.offTableUserInfoHeight + m.tableHeight + m.globalVerticalPadding,
      left:
        m.centerOfLeftTableCircle.left +
        (m.centerOfRightTableCircle.left - m.centerOfLeftTableCircle.left) * percentage,
    }
    return {
      position,
      calcPositionFromPixels: generateCalcPositionFromPixels(
        position,
        m.centerOfTable,
        (pixels) => pixels * 0.8,
      ),
    }
  }
  return percentageAlongSquareMiddle(0.5)
}

const calculateUserTablePositioning = (m: IPositioningMeasurements, usersCount: number) => {
  const otherUserPositioning = m.isLandscapeMode
    ? desktopCalculateOtherUserTablePositioning(m, usersCount)
    : mobileCalculateOtherUserTablePositioning(m, usersCount)
  const currentUserTablePositioning = m.isLandscapeMode
    ? desktopCalculateCurrentUserPositioning(m)
    : mobileCalculateCurrentUserPositioning(m)
  return { otherUserPositioning, currentUserTablePositioning }
}

export const usePositionProps = ({
  windowSize,
  otherUserCount,
  hasGameChatVisible,
}: {
  windowSize: {
    width: number
    height: number
  }
  otherUserCount: number
  hasGameChatVisible: boolean
}): IPositionProps => {
  const props = useMemo(() => {
    const measurements = getPositioningMeasurements(windowSize, hasGameChatVisible)
    const { currentUserTablePositioning, otherUserPositioning } = calculateUserTablePositioning(
      measurements,
      otherUserCount,
    )
    return { measurements, currentUserTablePositioning, otherUserPositioning }
  }, [windowSize.width, windowSize.height, hasGameChatVisible, otherUserCount])
  return props
}
